import { getToken } from "../../../../shared/data/storage/authStorage";
import { API_BASE_URL } from "../../../../config/api";
import type {
  ActionRequiredItemDTO,
  BooksHealthOverviewDTO,
  BooksHealthSummaryDTO,
  BooksMonthStatusItemDTO,
  MonthlyModuleStatusDTO,
  RecentBooksActivityDTO,
} from "../models/booksHealthDto";

export interface GetBooksHealthOverviewApiParams {
  companyId?: number;
  month?: string;
  year?: number;
}

/**
 * Fetch "Your Action Required" items for Books Health.
 * GET /api/client/books-health/action-required
 */
export async function getBooksHealthActionRequired(): Promise<
  ActionRequiredItemDTO[]
> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/client/books-health/action-required`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch action required items");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as ActionRequiredItemDTO[];
  }
  throw new Error("Invalid response from books health action-required API");
}

/**
 * Fetch Books Health summary for the effective month.
 * GET /api/client/books-health/summary
 */
export async function getBooksHealthSummary(): Promise<BooksHealthSummaryDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/client/books-health/summary`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch books health summary");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as BooksHealthSummaryDTO;
  }
  throw new Error("Invalid response from books health summary API");
}

/**
 * Fetch month-by-month books health status (default last 12).
 * GET /api/client/books-health/month-by-month?limit=12
 */
export async function getBooksHealthMonthByMonth(
  limit = 12,
): Promise<BooksMonthStatusItemDTO[]> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/client/books-health/month-by-month?limit=${encodeURIComponent(
    String(limit),
  )}`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch month-by-month status");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as BooksMonthStatusItemDTO[];
  }
  throw new Error("Invalid response from books health month-by-month API");
}

/**
 * Fetch monthly module status for effective month.
 * GET /api/client/books-health/monthly-module-status
 */
export async function getBooksHealthMonthlyModuleStatus(): Promise<
  MonthlyModuleStatusDTO[]
> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/client/books-health/monthly-module-status`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.message || "Failed to fetch books health monthly module status",
    );
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as MonthlyModuleStatusDTO[];
  }
  throw new Error("Invalid response from books health monthly module status API");
}

/**
 * Fetch recent activity for Books Health (current month, top 5).
 * GET /api/client/books-health/recent-activity
 */
export async function getBooksHealthRecentActivity(): Promise<
  RecentBooksActivityDTO[]
> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const url = `${API_BASE_URL}/api/client/books-health/recent-activity`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch recent activity");
  }

  const json = await res.json();
  if (json.success && json.data) {
    return json.data as RecentBooksActivityDTO[];
  }
  throw new Error("Invalid response from books health recent-activity API");
}

/**
 * Fetch Books Health overview for the client dashboard.
 * GET /api/client/books-health/overview
 * Uses mock data until backend endpoint is available.
 */
export async function getBooksHealthOverview(
  _params?: GetBooksHealthOverviewApiParams,
): Promise<BooksHealthOverviewDTO> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  // When backend is ready, uncomment and use:
  // const searchParams = new URLSearchParams();
  // if (params?.companyId != null) searchParams.set("companyId", String(params.companyId));
  // if (params?.month) searchParams.set("month", params.month);
  // if (params?.year != null) searchParams.set("year", String(params.year));
  // const url = `${API_BASE_URL}/api/client/books-health/overview?${searchParams.toString()}`;
  // const res = await fetch(url, {
  //   method: "GET",
  //   headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
  // });
  // if (!res.ok) {
  //   const err = await res.json().catch(() => ({}));
  //   throw new Error(err.message || "Failed to fetch books health overview");
  // }
  // const json = await res.json();
  // if (json.success && json.data) return json.data as BooksHealthOverviewDTO;
  // throw new Error("Invalid response from books health API");

  // Mock data for UI development
  return getMockBooksHealthOverview();
}

function getMockBooksHealthOverview(): BooksHealthOverviewDTO {
  return {
    action_required_items: [
      {
        id: "1",
        title: "OpenAI invoice amount unclear",
        description:
          "Arkaan needs you to confirm the correct amount before it can be posted.",
        raised_by: "Arkaan Sayed",
        date: "Apr 12",
        type: "query",
      },
    ],
    summary: {
      status: "on_track",
      reconciled_through_month: "Mar 2025",
      current_month_label: "April",
      current_month_progress_percent: 40,
      projected_close_date: "~22 Apr",
      summary_text:
        "Books reconciled through Mar 2025. April is 40% complete — closes ~22 Apr at current pace.",
      info_note:
        "Until April closes, March financials are your latest confirmed numbers.",
    },
    month_by_month_status: [
      { month_label: "May", year_label: "2024", status: "closed" },
      { month_label: "Jun", year_label: "2024", status: "closed" },
      { month_label: "Jul", year_label: "2024", status: "closed" },
      { month_label: "Aug", year_label: "2024", status: "closed" },
      { month_label: "Sep", year_label: "2024", status: "closed" },
      { month_label: "Oct", year_label: "2024", status: "closed" },
      { month_label: "Nov", year_label: "2024", status: "closed" },
      { month_label: "Dec", year_label: "2024", status: "closed" },
      { month_label: "Jan", year_label: "2025", status: "closed" },
      { month_label: "Feb", year_label: "2025", status: "closed" },
      { month_label: "Mar", year_label: "2025", status: "closed" },
      { month_label: "Apr", year_label: "2025", status: "open", percent_complete: 40 },
    ],
    monthly_module_status: [
      {
        module: "bills",
        label: "Vendor Bills",
        completed: 3,
        total: 8,
        pending_label: "5 pending",
        is_complete: false,
      },
      {
        module: "invoices",
        label: "Customer Invoices",
        completed: 2,
        total: 5,
        pending_label: "3 pending",
        is_complete: false,
      },
      {
        module: "bank",
        label: "Bank Transactions",
        completed: 14,
        total: 22,
        pending_label: "8 pending",
        is_complete: false,
      },
      {
        module: "journals",
        label: "Journal Entries",
        completed: 0,
        total: 0,
        pending_label: "All done",
        is_complete: true,
      },
    ],
    recent_activity: [
      {
        date: "Apr 16",
        description: "Categorized 14 bank transactions for April",
        user_name: "Arkaan Sayed",
      },
      {
        date: "Apr 15",
        description:
          "Posted 3 vendor bills (Meta, Prime Properties, Microsoft)",
        user_name: "Radhika",
      },
      {
        date: "Apr 14",
        description: "Processed 2 customer invoices (Alpha, Beta Tech)",
        user_name: "Yash",
      },
      {
        date: "Apr 12",
        description: "Raised query: OpenAI invoice amount unclear",
        user_name: "Arkaan Sayed",
      },
      {
        date: "Apr 10",
        description: "Completed March reconciliation — all items matched",
        user_name: "Chris Scott",
      },
    ],
    last_updated: "Apr 16",
    last_updated_by: "Arkaan Sayed",
  };
}
